import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineChevronLeft, HiOutlineRefresh, HiOutlineSearch } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { hasCardAction } from '../../utils/permissionHelper';
import PDFAnnotationModal from './PDFAnnotationModal';
import './InvoiceManagement.css';

const InvoiceManagement = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [modalMode, setModalMode] = useState(null); // 'pick' or 'verify'
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Permission check - only users with 'approve' action can verify and confirm delete
    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const canApprove = user?.role === 'system_admin' || hasCardAction(userPermissions, 'invoice-management', 'approve');

    useEffect(() => {
        fetchInvoices();
    }, [currentPage, searchTerm]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/invoices', {
                params: {
                    page: currentPage,
                    limit: 20,
                    search: searchTerm
                }
            });

            if (response.data.success) {
                setInvoices(response.data.data);
                setTotalPages(response.data.totalPages);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            alert('Failed to fetch invoices');
        } finally {
            setLoading(false);
        }
    };

    const handlePickClick = (invoice) => {
        setSelectedInvoice(invoice);
        setModalMode('pick');
    };

    const handleVerifyClick = (invoice) => {
        setSelectedInvoice(invoice);
        setModalMode('verify');
    };

    const handleConfirmDelete = async (invoice) => {
        if (!window.confirm('This will permanently delete the PDF file. Are you sure?')) return;

        try {
            const response = await axiosInstance.delete(`/api/invoices/${invoice.invoiceNumber}/confirm`);
            alert('Invoice PDF deleted successfully');
            
            // Update the invoice in state with the backend response
            if (response.data.success) {
                setInvoices(prevInvoices => 
                    prevInvoices.map(inv => 
                        inv.invoiceNumber === invoice.invoiceNumber 
                            ? { ...inv, pdfDeleted: true } 
                            : inv
                    )
                );
            }
        } catch (error) {
            console.error('Error deleting invoice:', error);
            alert(error.response?.data?.message || 'Failed to delete invoice');
        }
    };

    const handleRemarkChange = async (invoice, newRemark) => {
        try {
            await axiosInstance.patch(`/api/invoices/${invoice.invoiceNumber}/status`, {
                remark: newRemark
            });
        } catch (error) {
            console.error('Error updating remark:', error);
        }
    };

    const handleModalClose = () => {
        setSelectedInvoice(null);
        setModalMode(null);
        fetchInvoices(); // Refresh list after modal closes
    };

    if (loading && invoices.length === 0) {
        return (
            <div className="im-shell">
                <div className="im-loading"><div className="spinner"></div>Loading invoices...</div>
            </div>
        );
    }

    return (
        <div className="im-shell">
            {/* HEADER */}
            <header className="im-header">
                <div className="im-header-left">
                    <button className="im-back-btn" onClick={() => navigate('/purpose-selection')}>
                        <HiOutlineChevronLeft size={20} />
                    </button>
                    <div className="im-header-title">
                        <h1>Invoice Management</h1>
                        <span>Module</span>
                    </div>
                </div>
                <div className="im-header-right">
                    <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={fetchInvoices}
                        disabled={loading}
                    >
                        <HiOutlineRefresh /> {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </header>

            {/* BODY */}
            <div className="im-body">
                <main className="im-content">
                    {/* Search Bar */}
                    <div className="im-search-bar">
                        <div className="im-search-wrapper">
                            <HiOutlineSearch className="im-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by invoice number, branch ID or name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="im-search-input"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="im-table-wrap">
                        <div className="im-table-container">
                            <table className="im-table">
                                <thead>
                                    <tr>
                                        <th>Invoice Number</th>
                                        <th>Branch ID</th>
                                        <th>Branch Name</th>
                                        <th>Uploaded At</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                        <th>Remark</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="im-empty-table">
                                                No invoices found
                                            </td>
                                        </tr>
                                    ) : (
                                        invoices.map((invoice) => (
                                            <tr key={invoice._id}>
                                                <td><strong>{invoice.invoiceNumber}</strong></td>
                                                <td>{invoice.branchId}</td>
                                                <td>{invoice.branchName}</td>
                                                <td>{new Date(invoice.uploadedAt).toLocaleDateString()}</td>
                                                <td>
                                                    <div className="im-status-badges">
                                                        {invoice.pick && <span className="badge badge-picked">Picked</span>}
                                                        {invoice.verify && <span className="badge badge-verified">Verified</span>}
                                                        {!invoice.pick && !invoice.verify && <span className="badge badge-pending">Pending</span>}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="im-action-buttons">
                                                        {invoice.pdfDeleted ? (
                                                            <span className="badge badge-completed">Completed</span>
                                                        ) : invoice.verify ? (
                                                            canApprove && (
                                                                <button
                                                                    onClick={() => handleConfirmDelete(invoice)}
                                                                    className="btn btn-danger btn-sm"
                                                                >
                                                                    Delete
                                                                </button>
                                                            )
                                                        ) : invoice.pick ? (
                                                            canApprove && (
                                                                <button
                                                                    onClick={() => handleVerifyClick(invoice)}
                                                                    className="btn btn-warning btn-sm"
                                                                >
                                                                    Verify
                                                                </button>
                                                            )
                                                        ) : (
                                                            <button
                                                                onClick={() => handlePickClick(invoice)}
                                                                className="btn btn-success btn-sm"
                                                            >
                                                                Pick
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        placeholder="Add remark..."
                                                        defaultValue={invoice.remark || ''}
                                                        onBlur={(e) => handleRemarkChange(invoice, e.target.value)}
                                                        className="im-remark-input"
                                                    />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="im-pagination">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="btn btn-secondary btn-sm"
                            >
                                Previous
                            </button>
                            <span className="im-page-info">Page {currentPage} of {totalPages}</span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="btn btn-secondary btn-sm"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {selectedInvoice && modalMode && (
                <PDFAnnotationModal
                    invoice={selectedInvoice}
                    mode={modalMode}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default InvoiceManagement;
